import { ref, reactive, computed, watch } from 'vue';
import { Form } from 'ant-design-vue';

import i18n from '@/i18n';

import Utils from '@/api/Utils';

import { hasCoords } from '@/utils/baseStationsUtils';

const t = i18n.global.t;

const alphaNumericChecker = value => {
	const valid = value == '' || /^[a-zA-Z0-9_]*[a-zA-Z0-9]?$/.test(value);
	return { valid, value };
};

const mountpointCheckerCore = value => {
	const valid = value == '' || /^[A-Za-z0-9_.-]+$/.test(value);
	return { valid, value };
};

const mountpointChecker = value => {
	const result = mountpointCheckerCore(value);
	if (!result.valid) {
		return result;
	}
	return {
		valid: result.valid,
		value: result.value.toUpperCase(),
	};
};

const latinCharactersChecker = value => {
	const valid = value == '' || /^[- a-zA-Z0-9_.,;:!?@#$%^&*()+]*$/.test(value);
	return { valid, value };
};

const antennaChecker = value => {
	const valid = value == '' || /^[- a-zA-Z0-9_.,;:!?@#$%^&*()+/]*$/.test(value);
	return { valid, value };
};

const hostnameChecker = value => {
	const valid = value == '' || /^\[?[a-zA-Z0-9:][-a-zA-Z0-9_.:]*[a-zA-Z0-9:]?\]?$/.test(value);
	return { valid, value };
};

const asciiChecker = value => {
	let valid = value == '' || /^[\x20-\x7E]+$/.test(value);
	return { valid, value };
};

const asciiNoColonChecker = value => {
	let valid = value == '' || /^[\x20-\x7E]+$/.test(value);
	if (value.indexOf(':') > -1) {
		valid = false;
	}
	return { valid, value };
};

const publicHostnameChecker = value => {
	const exp = new RegExp('^(http://|https://)?[-a-zA-Zа-яА-Я0-9]+\\.[-a-zA-Zа-яА-Я0-9]+[-a-zA-Zа-яА-Я0-9.]+$');
	const valid = value == '' || exp.test(value);
	return { valid, value };
};

const filenameChecker = value => {
	const valid = value == '' || /^[-a-zA-Z0-9_./!()]+$/.test(value);
	return { valid, value };
};

const ANT_MAX_LENGTH = 20;
const MNTPNT_INT_MAX_LENGTH = 9;
const SERVICE_NAME_MIN_LENGTH = 2;
const SERVICE_NAME_MAX_LENGTH = 20;

const isProtocolSecureRequired = formState => {
	return formState.protocol === 'tcp_server' || formState.protocol === 'ntrip_caster';
}

const isProtocolSecureHelpVisible = formState => {
	return formState.protocol === 'ntrip_caster';
}

const isHostRequired = formState => {
	return ['ntrip_caster', 'tcp_server'].includes(formState.protocol);
}

const isPortRequired = formState => {
	return formState.protocol !== 'ntrip_server';
}

const isCredentialsRequired = formState => {
	return ['ntrip_caster', 'ntrip_server'].includes(formState.protocol);
}

const isMntpntSrcRequired = formState => {
	return formState.protocol === 'ntrip_caster';
}

const isRequestVisible = formState => {
	return formState.protocol === 'tcp_server';
}

const isRemoteAddressVisible = formState => {
	return formState.protocol === 'tcp_client';
}

const isCasterAddressVisible = formState => {
	return formState.protocol === 'ntrip_server';
}

const isFieldNonEmpty = field => {
	return formState => formState[field].length > 0;
}

const requireField = messageId => {
	return async (_rule, value) => {
		if (!value && value !== 0) {
			return Promise.reject(t('validators.' + messageId));
		}
		return Promise.resolve();
	};
};

const requireFieldWithCondition = (formState, conditionFunc, messageId) => {
	return async (_rule, value) => {
		if (conditionFunc(formState) && !Utils.safeTrim(value)) {
			return Promise.reject(t('validators.' + messageId));
		}
		return Promise.resolve();
	};
};

const requireNonEmptyArray = messageId => {
	return async (_rule, value) => {
		if (!Array.isArray(value) || value.length === 0) {
			return Promise.reject(t('validators.' + messageId));
		}
		return Promise.resolve();
	};
};


const hostsValidator = async (_rule, value) => {
	if (!value) {
		return Promise.reject(t('validators.hostsRequiredMessage'));
	}
	const lines = value.split('\n');
	for (let index = 0; index < lines.length; index++) {
		let line = lines[index].trim();
		let { valid } = publicHostnameChecker(line);
		if (!valid) {
			return Promise.reject(t('validators.hostsRequiredMessageIndex', {
				lineNum: index + 1,
				value: line,
			}));
		}
	}
	return Promise.resolve();
};


const entrypointNameValidator = async (_rule, value) => {
	if (!value) {
		return Promise.reject(t('validators.entrypointNameRequiredMessage'));
	}
	if (value.length < SERVICE_NAME_MIN_LENGTH) {
		return Promise.reject(t('validators.entrypointNameFormatMessage'));
	}
	return Promise.resolve();
};


const useAddBaseStationFormRules = (formState, formSettings) => {
	const protocolTcpClient = computed(() => formState.protocol === 'tcp_client');
	const portMinValue = computed(() => protocolTcpClient.value ? formSettings.tcpPortMinValue : 1);
	const portMaxValue = computed(() => protocolTcpClient.value ? formSettings.tcpPortMaxValue : 65535);
	const portRange = computed(() => {
		if (protocolTcpClient.value) {
			return formSettings.tcpPortError.length > 0 ? 'invalid' : 'all';
		}
		return 'port';
	});
	const validateHost = requireFieldWithCondition(formState, isHostRequired, 'hostRequiredMessage');

	const validatePort = async (_rule, value) => {
		const strValue = Utils.safeTrim(value);
		if (isPortRequired(formState) && !strValue) {
			return Promise.reject(t('validators.portRequiredMessage'));
		}
		if (strValue) {
			if (protocolTcpClient.value && formSettings.tcpPortError.length > 0) {
				return Promise.reject(t('validators.noValidValuesMessage'));
			}
			const numValue = Number(strValue);
			if (isNaN(numValue) || numValue < portMinValue.value || numValue > portMaxValue.value) {
				return Promise.reject(t('validators.integerInRangeRequiredMessage', [portMinValue.value, portMaxValue.value]));
			}
		}
		return Promise.resolve();
	};

	const validateMntpntSrc = requireFieldWithCondition(formState, isMntpntSrcRequired, 'mntpntSrcRequiredMessage');

	const validateAntReferencePoint = async (_rule, value) => {
		if (hasCoords(formState)) {
			if (value === 'NA') {
				return Promise.reject(t('validators.antReferencePointValueRequiredMessage'));
			}
		} else {
			if (value !== 'NA') {
				return Promise.reject(t('validators.antReferencePointNARequiredMessage'));
			}
		}
		return Promise.resolve();
	};

	const rules = reactive({
		label: [{ validator: requireField('labelRequiredMessage'), trigger: 'change' }],
		protocol: [{ validator: requireField('protocolRequiredMessage'), trigger: 'change' }],
		antReferencePoint: [{ validator: validateAntReferencePoint, trigger: 'change' }],

		host: [{ validator: validateHost, trigger: 'change' }],
		port: [{ validator: validatePort, trigger: 'change' }],

		mntpntInt: [{ validator: requireField('mntpntIntRequiredMessage'), trigger: 'change' }],
		mntpntSrc: [{ validator: validateMntpntSrc, trigger: 'change' }],
	});

	const { validate, validateInfos } = Form.useForm(formState, rules);
	watch([
		() => formState.xCoord,
		() => formState.yCoord,
		() => formState.zCoord,
	], () => { validate(['antReferencePoint'], { flush: 'post' }) });
	return {
		validate,
		validateInfos,
		protocolTcpClient,
		portMinValue,
		portMaxValue,
		portRange,
	}
}


const useAddBaselineFormRules = formState => {
	// const roverValidator = async (_rule, value) => {
	// 	if (value === formState.base) {
	// 		return Promise.reject(t('validators.roverAndBaseSameMessage'));
	// 	}
	// 	return Promise.resolve();
	// };

	const rules = reactive({
		base: [{ validator: requireField('baseStationRequiredMessage'), trigger: 'change' }],
		rover: [{ validator: requireField('baseStationRequiredMessage'), trigger: 'change' }],
		description: [{ required: false }],
		selectedGnss: [{ validator: requireNonEmptyArray('selectedGnssRequiredMessage'), trigger: 'change' }],
		// rover: [{ validator: roverValidator, trigger: 'change' }],
	});
	return Form.useForm(formState, rules);
}

const useRoverPerformanceTestFormRules = formState => {
	const validatePoints = async (_rule, value) => {
		if (!Array.isArray(value) || value.length < 2) {
			return Promise.reject(t('validators.pointsMinLengthMessage'));
		}
		for (const point of value) {
			if (point.lat == '' || point.lon == '') {
				return Promise.reject(t('validators.pointRequiredMessage'));
			}
		}
		if (value.length < 2) {
			return Promise.reject(t('validators.pointsMinLengthMessage'));
		}
		return Promise.resolve();
	};

	const validateStartDelaySec = async (_rule, value) => {
		if (value < 0.050) {
			return Promise.reject(t('validators.startDelaySecMinValueMessage'));
		}
		return Promise.resolve();
	};

	const rules = computed(() => {
		const baseRules = {
			mountpoint: [{ validator: requireField('mntpntSrcRequiredMessage'), trigger: 'change' }],
			points: [{ validator: validatePoints, trigger: 'change' }],
			altitude: [{ validator: requireField('integerRequiredMessage'), trigger: 'change' }],
			startDelaySec: [{ validator: validateStartDelaySec, trigger: 'change' }],
		};

		if (formState.mode === 'area') {
			baseRules.stepLatKm = [{ validator: requireField('decimalRequiredMessage'), trigger: 'change' }];
			baseRules.stepLonKm = [{ validator: requireField('decimalRequiredMessage'), trigger: 'change' }];
			baseRules.maxRoversAmount = [{ validator: requireField('integerRequiredMessage'), trigger: 'change' }];
		} else if (formState.mode === 'route') {
			baseRules.roversAmount = [{ validator: requireField('integerRequiredMessage'), trigger: 'change' }];
			baseRules.roversVelocityKph = [{ validator: requireField('integerRequiredMessage'), trigger: 'change' }];
		}

		return baseRules;
	});

	return Form.useForm(formState, rules);
}

const useAddUserFormRules = formState => {
	const rules = reactive({
		username: [{ validator: requireField('usernameRequiredMessage'), trigger: 'change' }],
		role: [{ validator: requireField('roleRequiredMessage'), trigger: 'change' }],
	});
	return Form.useForm(formState, rules)
}

const useGeoFencingFormRules = formState => {
	const validateEnabledRequired = async (_rule, value) => {
		if (formState.enabled && !value) {
			return Promise.reject(t('validators.parameterRequiredForEnabledStateMessage'));
		}
		return Promise.resolve();
	};

	const rules = reactive({
		enabled: [{ required: false }],
		bCoord: [{ validator: validateEnabledRequired, trigger: 'change' }],
		lCoord: [{ validator: validateEnabledRequired, trigger: 'change' }],
		radius: [{ validator: validateEnabledRequired, trigger: 'change' }],
	});
	return Form.useForm(formState, rules)
}

const useVRSSettingsFtpFormRules = formState => {
	const rules = reactive({
		userProtocol: [{ required: true }],
		host: [{ validator: requireField('hostRequiredMessage'), trigger: 'change' }],
		port: [{ validator: requireField('portRequiredMessage'), trigger: 'change' }],
		username: [{ required: false }],
		password: [{ required: false }],
		fullFilename: [{ validator: requireField('fullFilenameRequiredMessage'), trigger: 'change' }],
	});
	return Form.useForm(formState, rules);
}

const useLeapSecondsFormRules = formState => {
	const rules = reactive({
		year: [{ required: true, message: 'Year is required' }],
		monthDay: [{ required: true, message: 'Date is required' }],
		value: [{ required: true, message: 'Value is required' }],
	});
	return Form.useForm(formState, rules);
}

const useVRSSettingsCasterIpFormRules = formState => {
	const rules = reactive({
		value: [{ validator: requireField('hostRequiredMessage'), trigger: 'change' }],
	});
	return Form.useForm(formState, rules);
}

const useVRSSettingsCleanerFormRules = formState => {
	const rules = reactive({
		expirationDaysLogs: [{ validator: requireField('integerRequiredMessage'), trigger: 'change' }],
		expirationDaysRinexFiles: [{ validator: requireField('integerRequiredMessage'), trigger: 'change' }],
		expirationDaysRinexStats: [{ validator: requireField('integerRequiredMessage'), trigger: 'change' }],
		expirationDaysNmeaFiles: [{ validator: requireField('integerRequiredMessage'), trigger: 'change' }],
		expirationDaysProducts: [{ validator: requireField('integerRequiredMessage'), trigger: 'change' }],
	});
	return Form.useForm(formState, rules);
}

const useVRSSettingsDistancesFormRules = formState => {
	const rules = reactive({
		radiusVrs: [{ validator: requireField('decimalRequiredMessage'), trigger: 'change' }],
		radiusNear: [{ validator: requireField('decimalRequiredMessage'), trigger: 'change' }],
		nameVrs: [{ validator: entrypointNameValidator, trigger: 'change' }],
		nameNear: [{ validator: entrypointNameValidator, trigger: 'change' }],
		nameCssr: [{ validator: entrypointNameValidator, trigger: 'change' }],
		nameBrdc: [{ validator: entrypointNameValidator, trigger: 'change' }],
	});
	return Form.useForm(formState, rules);
}

const useSubNetworksOtherFormRules = formState => {
	const rules = reactive({
		title: [{ validator: requireField('titleRequiredMessage'), trigger: 'change' }],
		radiusVrs: [{ validator: requireField('decimalRequiredMessage'), trigger: 'change' }],
		procd: [{ validator: requireField('integerRequiredMessage'), trigger: 'change' }],
		measurementsTimeInterval: [{ validator: requireField('integerRequiredMessage'), trigger: 'change' }],
		gssMaxDist: [{ validator: requireField('integerRequiredMessage'), trigger: 'change' }],
		closestGssMinCount: [{ validator: requireField('integerRequiredMessage'), trigger: 'change' }],
		closestMaxDist: [{ validator: requireField('integerRequiredMessage'), trigger: 'change' }],
		gssMaxCount: [{ validator: requireField('integerRequiredMessage'), trigger: 'change' }],
		convBaseStationsThreshold: [{ validator: requireField('percentsRequiredMessage'), trigger: 'change' }],
		convSatellitesThreshold: [{ validator: requireField('percentsRequiredMessage'), trigger: 'change' }],
	});
	return Form.useForm(formState, rules);
}

const useAddSubNetworkFormRules = formState => {
	const rules = reactive({
		label: [{ validator: requireField('labelRequiredMessage'), trigger: 'change' }],
		title: [{ validator: requireField('titleRequiredMessage'), trigger: 'change' }],
	});
	return Form.useForm(formState, rules);
}

const useAddRtcmfMountpointFormRules = formState => {
	const rules = reactive({
		label: [{ validator: requireField('labelRequiredMessage'), trigger: 'change' }],
	});
	return Form.useForm(formState, rules);
}


const useVRSSettingsOtherSettingsFormRules = formState => {
	const rules = reactive({
		ntpHosts: [{ validator: hostsValidator, trigger: 'change' }],
	});
	return Form.useForm(formState, rules);
}


const useCNetUpdateFormRules = formState => {
	const rules = reactive({
		cnetNumber: [{ validator: requireField('cnetNumberRequiredMessage'), trigger: 'change' }],
		week: [{ validator: requireField('weekRequiredMessage'), trigger: 'change' }],
	});
	return Form.useForm(formState, rules);
}


const useVRSSettingsPaasSettingsFormRules = formState => {
	const rules = reactive({});
	return Form.useForm(formState, rules);
}

const useVRSSettingsTerrestrialReferenceSystemFormRules = formState => {
	const oneParamRequired = [{ validator: requireField('oneParamRequiredMessage'), trigger: 'blur' }];
	const manyParamsRequired = [{ validator: requireField('manyParamsRequiredMessage'), trigger: 'blur' }];
	const rules = reactive({
		label: oneParamRequired,
		t1: manyParamsRequired,
		t2: manyParamsRequired,
		t3: manyParamsRequired,
		scale: oneParamRequired,
		r1: manyParamsRequired,
		r2: manyParamsRequired,
		r3: manyParamsRequired,
		dt1: manyParamsRequired,
		dt2: manyParamsRequired,
		dt3: manyParamsRequired,
		dscale: oneParamRequired,
		dr1: manyParamsRequired,
		dr2: manyParamsRequired,
		dr3: manyParamsRequired,
		epochDate: [{ required: false }],
		epoch2Date: [{ required: false }],
		velocity1: manyParamsRequired,
		velocity2: manyParamsRequired,
		velocity3: manyParamsRequired,
	});
	return Form.useForm(formState, rules);
}

const getSecureProtocolOptions = formState => {
	if (formState.protocol === 'ntrip_caster') {
		return [
			{ label: 'ntrip', value: false },
			{ label: 'ntrips', value: true },
		];
	}
	if (formState.protocol === 'tcp_server') {
		return [
			{ label: 'tcp', value: false },
			{ label: 'tls', value: true },
		];
	}
	return [];
}

const useExternalSourceFormRules = formState => {
	const rules = reactive({
		host: [{ validator: requireField('hostRequiredMessage'), trigger: 'change' }],
		port: [{ validator: requireField('portRequiredMessage'), trigger: 'change' }],
		username: [{ validator: requireField('usernameRequiredMessage'), trigger: 'change' }],
		password: [{ validator: requireField('passwordRequiredMessage'), trigger: 'change' }],
		mntpntSrc: [{ validator: requireField('mntpntSrcRequiredMessage'), trigger: 'change' }],
	});
	return Form.useForm(formState, rules);
}

const useStrImportFormRules = formState => {
	const validateUsername = requireFieldWithCondition(formState, isFieldNonEmpty('password'), 'userPassRequiredTogetherMessage');
	const validatePassword = requireFieldWithCondition(formState, isFieldNonEmpty('username'), 'userPassRequiredTogetherMessage');
	const rules = reactive({
		host: [{ validator: requireField('hostRequiredMessage'), trigger: 'change' }],
		port: [{ validator: requireField('portRequiredMessage'), trigger: 'change' }],
		username: [{ validator: validateUsername, trigger: 'change' }],
		password: [{ validator: validatePassword, trigger: 'change' }],
	});
	return Form.useForm(formState, rules);
}


const useMapLegendFormRules = formState => {
	const rules = reactive({
		title: [{ validator: requireField('titleRequiredMessage'), trigger: 'change' }],
		step: [{ validator: requireField('stepRequiredMessage'), trigger: 'change' }],
	});
	return Form.useForm(formState, rules)
}


const useWatchFormChanges = (formState, statuses, name) => {
	const changedHandler = isChanged => {
		statuses[name] = isChanged;
	};
	const isWatching = ref(false);
	watch(
		() => formState,
		() => {
			if (isWatching.value) {
				changedHandler(true);
			}
		},
		{ deep: true }
	)
	const enableWatch = () => {
		isWatching.value = true;
	};
	const pauseWatch = () => {
		isWatching.value = false;
	};
	const resetChangedState = () => {
		enableWatch();
		changedHandler(false);
	};
	return { enableWatch, pauseWatch, resetChangedState }
}


export default {
	alphaNumericChecker,
	mountpointChecker,
	latinCharactersChecker,
	antennaChecker,
	asciiChecker,
	asciiNoColonChecker,
	hostnameChecker,
	filenameChecker,
	ANT_MAX_LENGTH,
	MNTPNT_INT_MAX_LENGTH,
	SERVICE_NAME_MAX_LENGTH,
	useAddBaseStationFormRules,
	useAddBaselineFormRules,
	useRoverPerformanceTestFormRules,
	useAddUserFormRules,
	useGeoFencingFormRules,
	useVRSSettingsFtpFormRules,
	useLeapSecondsFormRules,
	useVRSSettingsCasterIpFormRules,
	useVRSSettingsCleanerFormRules,
	useVRSSettingsDistancesFormRules,
	useSubNetworksOtherFormRules,
	useAddSubNetworkFormRules,
	useAddRtcmfMountpointFormRules,
	useVRSSettingsOtherSettingsFormRules,
	useCNetUpdateFormRules,
	useVRSSettingsPaasSettingsFormRules,
	useVRSSettingsTerrestrialReferenceSystemFormRules,
	isProtocolSecureRequired,
	isProtocolSecureHelpVisible,
	isHostRequired,
	isPortRequired,
	isCredentialsRequired,
	isMntpntSrcRequired,
	isRequestVisible,
	isRemoteAddressVisible,
	isCasterAddressVisible,
	getSecureProtocolOptions,
	useExternalSourceFormRules,
	useStrImportFormRules,
	useMapLegendFormRules,
	useWatchFormChanges,
}
